import './plan.css';
import { IonButton, IonContent } from '@ionic/react';
import { useState } from 'react';
import type { IsoDate } from '../../domain/dates';
import { useMessages } from '../i18n/LanguageProvider';
import { PageHeader } from '../PageHeader';
import { useAppData } from '../state/AppDataProvider';
import { currentPlan, nextPlan } from '../state/plan-service';
import { randomSeed } from '../state/seed';
import { usePlanEdits } from '../state/use-plan-edits';
import { offersNextPlan } from '../today/today-summary';
import { DayDetail } from './DayDetail';
import { PlanPrompt } from './PlanPrompt';
import { PlanSection } from './PlanSection';

function PlanAgainQuestion({ onDone }: { onDone: () => void }) {
  const plans = usePlanEdits();
  const { t } = useMessages();
  const [busy, setBusy] = useState(false);
  const confirm = () => {
    setBusy(true);
    void plans.edit({ kind: 'plan-again', seed: randomSeed() }).finally(onDone);
  };
  return (
    <section className="app-card">
      <p className="app-lead">{t.planAgainQuestion}</p>
      {busy ? (
        <p className="app-muted">{t.planning}</p>
      ) : (
        <div className="app-actions">
          <IonButton onClick={confirm}>{t.yes}</IonButton>
          <IonButton fill="outline" onClick={onDone}>
            {t.cancel}
          </IonButton>
        </div>
      )}
    </section>
  );
}

/**
 * DE: Bildschirm „Plan“: laufender Plan nach Wochen, nächster Plan darunter, Tage zum Ändern.
 * EN: Plan screen: running plan by week, the next plan below, days to change.
 */
export function PlanPage() {
  const { data, today } = useAppData();
  const { t } = useMessages();
  const [selected, setSelected] = useState<IsoDate>();
  const [asking, setAsking] = useState(false);
  const current = currentPlan(data, today);
  const next = nextPlan(data, today);
  return (
    <>
      <PageHeader title={t.tabPlan}>
        {current && (
          <IonButton fill="clear" onClick={() => setAsking(true)}>
            {t.planAgain}
          </IonButton>
        )}
      </PageHeader>
      <IonContent>
        <div className="app-page">
          {asking && <PlanAgainQuestion onDone={() => setAsking(false)} />}
          {current && <PlanSection stored={current} onSelect={setSelected} />}
          {!current && !next && <PlanPrompt mode="first" />}
          {offersNextPlan(data, today) && <PlanPrompt mode="next" />}
          {next && <h2 className="app-section-title">{t.nextPlanTitle}</h2>}
          {next && <PlanSection stored={next} onSelect={setSelected} />}
        </div>
        <DayDetail date={selected} onClose={() => setSelected(undefined)} />
      </IonContent>
    </>
  );
}
